export const LEGAL_PAGES = {
  privacy: {
    title: "Privacy Policy",
    effective: "Effective date: June 1, 2026",
    intro:
      "Medlyn 360 (\"Medlyn 360,\" \"we,\" \"us\") provides Virtual Live Human Medical Scribing services to physicians and medical practices in the United States. This Privacy Policy explains how we collect, use, and protect information submitted through this website (medlyn360.com). It applies to this website only; the handling of protected health information (PHI) during scribing engagements is governed separately by our service agreements and Business Associate Agreements (BAAs).",
    sections: [
      {
        h: "1. Information We Collect",
        p: [
          "Information you provide: when you submit our contact or booking forms, we collect the details you enter — typically your name, practice name, email address, phone number, specialty, preferred call time, and your message.",
          "Information collected automatically: our hosting infrastructure may record standard technical logs (such as IP address, browser type, and pages requested) for security and operational purposes. This website does not use advertising trackers, analytics pixels, or third-party marketing cookies.",
          "Browser storage: we store a single preference in your browser's local storage to remember your light/dark theme choice. This is a functional preference and is not used to track you.",
        ],
      },
      {
        h: "2. How We Use Your Information",
        p: [
          "We use the information you submit to respond to your enquiry, schedule and conduct discovery calls, provide and improve our services, and maintain ordinary business records. We do not use your information for third-party advertising, and we do not sell, rent, or trade your personal information to anyone.",
        ],
      },
      {
        h: "3. Do Not Submit Patient Information",
        p: [
          "This website is not intended for the transmission of protected health information. Please do not include patient names, medical record numbers, clinical details, or any other PHI in website forms or emails. PHI exchanged during a scribing engagement is handled exclusively through secure, access-controlled channels under a signed BAA.",
        ],
      },
      {
        h: "4. How We Share Information",
        p: [
          "We share information only with service providers who help us operate this website and respond to enquiries — such as our hosting provider and transactional email provider — and only to the extent necessary for those purposes. We may also disclose information where required by law, regulation, or legal process, or to protect our rights and the safety of others.",
        ],
      },
      {
        h: "5. Cookies and Analytics",
        p: [
          "With your consent, we use privacy-friendly, first-party analytics to understand which pages and services visitors are interested in (for example, page views and clicks on key buttons). This data is stored on our own infrastructure, is not shared with advertising networks, and is not used to build advertising profiles. You can decline analytics in the consent banner and the website works identically. We use no third-party advertising trackers or marketing pixels.",
        ],
      },
      {
        h: "6. Data Retention",
        p: [
          "We retain enquiry and booking submissions for as long as reasonably necessary to respond to you, evaluate a potential engagement, and maintain business records. You may request deletion of your submission at any time using the contact details below.",
        ],
      },
      {
        h: "7. Your Privacy Rights",
        p: [
          "Depending on your state of residence (including under the California Consumer Privacy Act as amended by the CPRA, and similar laws in states such as Virginia, Colorado, Connecticut, and Texas), you may have the right to know what personal information we hold about you, to request a copy of it, to correct it, or to request its deletion. We do not sell personal information and do not share it for cross-context behavioral advertising, so there is nothing to opt out of in that regard.",
          "To exercise any of these rights, email us at hello@medlyn360.com. We will verify your request and respond within the timeframe required by applicable law. We will not discriminate against you for exercising your privacy rights.",
        ],
      },
      {
        h: "8. Security",
        p: [
          "We use commercially reasonable safeguards to protect the information submitted through this website, including encryption in transit (HTTPS) and access controls on stored enquiries. No method of transmission or storage is completely secure, and we cannot guarantee absolute security.",
        ],
      },
      {
        h: "9. Children's Privacy",
        p: [
          "This website is intended for healthcare professionals and business audiences. It is not directed to children under 16, and we do not knowingly collect personal information from children.",
        ],
      },
      {
        h: "10. Changes to This Policy",
        p: [
          "We may update this Privacy Policy from time to time. The effective date at the top of this page reflects the most recent revision. Material changes will be posted on this page.",
        ],
      },
      {
        h: "11. Contact Us",
        p: [
          "For privacy questions or requests, contact Medlyn 360 at hello@medlyn360.com.",
        ],
      },
    ],
  },
  terms: {
    title: "Terms of Use",
    effective: "Effective date: June 1, 2026",
    intro:
      "These Terms of Use (\"Terms\") govern your access to and use of the Medlyn 360 website (medlyn360.com). By using this website, you agree to these Terms. If you do not agree, please do not use the website.",
    sections: [
      {
        h: "1. About This Website",
        p: [
          "This website provides information about Medlyn 360's Virtual Live Human Medical Scribing services. The scribing services themselves are provided under separate written service agreements and Business Associate Agreements between Medlyn 360 and each client practice. Nothing on this website constitutes an offer that becomes binding without such an agreement.",
        ],
      },
      {
        h: "2. Informational Purposes Only — No Professional Advice",
        p: [
          "Content on this website — including blog articles, research summaries, statistics, and the ROI calculator — is provided for general informational purposes only. It does not constitute medical, legal, coding, billing, or financial advice, and it should not be relied upon as a substitute for advice from qualified professionals familiar with your specific circumstances.",
        ],
      },
      {
        h: "3. Estimates and Research References",
        p: [
          "Figures presented on this website (such as potential cost savings, throughput ranges, and ROI calculator outputs) are estimates derived from published research, industry benchmarks, and stated assumptions. They are illustrative only, vary by practice, and are not a guarantee of savings, revenue, or performance.",
        ],
      },
      {
        h: "4. No Patient Information",
        p: [
          "You agree not to submit protected health information or any patient-identifying information through this website's forms or email links. PHI is handled only within contracted scribing engagements under a signed BAA.",
        ],
      },
      {
        h: "5. Acceptable Use",
        p: [
          "You agree not to misuse this website, including by attempting to gain unauthorized access to systems or data, interfering with the website's operation, scraping content at scale, submitting false or misleading enquiries, or using the website for any unlawful purpose.",
        ],
      },
      {
        h: "6. Intellectual Property",
        p: [
          "The Medlyn 360 name, logo, website design, and content are owned by Medlyn 360 or its licensors and are protected by intellectual property laws. You may view and share links to our content for personal or internal business purposes; any other reproduction or use requires our prior written permission.",
        ],
      },
      {
        h: "7. Third-Party Links",
        p: [
          "This website links to third-party sources and publications (for example, research citations). Those sites are governed by their own terms and privacy policies, and we are not responsible for their content.",
        ],
      },
      {
        h: "8. Disclaimer of Warranties",
        p: [
          "This website is provided \"as is\" and \"as available.\" To the fullest extent permitted by law, Medlyn 360 disclaims all warranties, express or implied, including warranties of merchantability, fitness for a particular purpose, accuracy, and non-infringement. We do not warrant that the website will be uninterrupted, error-free, or free of harmful components.",
        ],
      },
      {
        h: "9. Limitation of Liability",
        p: [
          "To the fullest extent permitted by law, Medlyn 360 will not be liable for any indirect, incidental, special, consequential, or punitive damages, or for any loss of profits, revenue, or data, arising out of or related to your use of this website. This limitation does not apply to liability that cannot be limited under applicable law and does not modify any rights or obligations set out in a signed service agreement.",
        ],
      },
      {
        h: "10. Changes to These Terms",
        p: [
          "We may revise these Terms from time to time. The effective date above reflects the most recent revision, and continued use of the website after changes are posted constitutes acceptance of the revised Terms.",
        ],
      },
      {
        h: "11. Governing Law",
        p: [
          "These Terms are governed by the laws of the United States and the state in which Medlyn 360 is organized, without regard to conflict-of-law principles.",
        ],
      },
      {
        h: "12. Contact",
        p: ["Questions about these Terms can be sent to hello@medlyn360.com."],
      },
    ],
  },
  hipaa: {
    title: "HIPAA Statement",
    effective: "Last reviewed: June 2026",
    intro:
      "Privacy and security are foundational to how Medlyn 360 delivers Virtual Live Human Medical Scribing. This statement summarizes our approach to HIPAA compliance for prospective and current client practices.",
    sections: [
      {
        h: "1. Our Role Under HIPAA",
        p: [
          "When providing scribing services to a covered entity, Medlyn 360 operates as a Business Associate under the Health Insurance Portability and Accountability Act (HIPAA). Every engagement is backed by a signed Business Associate Agreement (BAA) before any protected health information is accessed.",
        ],
      },
      {
        h: "2. Safeguards",
        p: [
          "Administrative safeguards: every scribe completes HIPAA privacy and security training before joining encounters, with ongoing compliance reviews and audits. Access to client environments is role-based and limited to assigned personnel.",
          "Technical safeguards: scribing sessions run over encrypted, access-controlled channels. Documentation is performed directly inside the client's EHR under credentials and permissions controlled by the practice.",
          "Data minimization: our workflow is designed so that documentation lives in your EHR. We do not operate an ambient recording product — there is no retained audio corpus and no model training on patient speech.",
        ],
      },
      {
        h: "3. This Website and PHI",
        p: [
          "This marketing website is not a channel for protected health information. Please do not submit patient details through website forms or email. PHI is exchanged only within contracted engagements through secure, agreed-upon channels.",
        ],
      },
      {
        h: "4. Incident Response",
        p: [
          "We maintain procedures for identifying, containing, and reporting security incidents, including breach notification obligations to client practices consistent with the BAA and applicable law.",
        ],
      },
      {
        h: "5. Questions and BAA Requests",
        p: [
          "Compliance officers and practice administrators are welcome to request our BAA and discuss security practices in detail. Contact us at hello@medlyn360.com or book a discovery call.",
        ],
      },
    ],
  },
};
